import type {
  AberturaPlanta,
  CaminoPlanta,
  MuellePlanta,
  NavePlanta,
  Planta as DatosPlanta,
  RackPlanta,
  SectorPlanta,
} from '../../api';
import { c, colorAbertura, colorCamino } from '../../theme';
import { HOLGURA_M } from '../../tres/geometria';
import { claveDeLado, claveDePared, LADOS, ladoDeNave, type Dibujo, type Punto, type Tipo } from './arrastre';

/**
 * El espesor del perímetro de una nave no se guarda ni viaja por la API: nace
 * del rectángulo de la nave, igual que `ESPESOR_PERIMETRAL_M` en
 * `backend/src/domain/cerramientos.ts`. Se repite acá como constante, para
 * que el plano lo dibuje en su espesor real y no con un número inventado.
 */
const ESPESOR_PERIMETRAL_M = 0.2;

// Con qué se pinta cada tipo de abertura: `COLOR_ABERTURA`/`colorAbertura`
// en `theme.ts`, junto a `COLOR_CAMINO` — una sola tabla para toda la app.

/** ¿Interrumpe el muro a nivel del piso, o hay pared abajo? Igual que `llegaAlPiso` en cerramientos.ts. */
const llegaAlPiso = (a: Pick<AberturaPlanta, 'antepecho'>) => a.antepecho <= HOLGURA_M;

export default function PlanoEditable({
  datos,
  svgRef,
  seleccion,
  preview,
  colorDe,
  onBajar,
  onBajarMuelle,
  onBajarCamino,
  onBajarSector,
  onBajarNave,
  dibujando,
  trazo,
  trazoPared,
  colorTrazo,
  onEmpezarTrazo,
  onMover,
  onSoltar,
}: {
  datos: DatosPlanta;
  svgRef: React.RefObject<SVGSVGElement>;
  seleccion: string | null;
  preview: { tipo: Tipo; id: string; x: number; y: number } | null;
  colorDe: (id: string) => string;
  onBajar: (e: React.PointerEvent, r: RackPlanta) => void;
  onBajarMuelle: (e: React.PointerEvent, m: MuellePlanta) => void;
  onBajarCamino: (e: React.PointerEvent, k: CaminoPlanta) => void;
  onBajarSector: (e: React.PointerEvent, s: SectorPlanta) => void;
  onBajarNave: (e: React.PointerEvent, n: NavePlanta) => void;
  dibujando: Dibujo | null;
  trazo: { x: number; y: number; ancho: number; largo: number } | null;
  /** El trazo de una pared es un segmento, no un rectángulo: va aparte. */
  trazoPared: { a: Punto; b: Punto } | null;
  colorTrazo: { relleno: string; borde: string } | undefined;
  onEmpezarTrazo: (e: React.PointerEvent) => void;
  onMover: (e: React.PointerEvent) => void;
  onSoltar: () => void;
}) {
  const { ancho, largo } = datos.predio;
  const margen = 2;

  /**
   * El rincón de cada nave, que es lo que se le suma a todo lo de adentro.
   * Mientras se arrastra una nave el origen sale del `preview` y no de lo
   * guardado, así sus racks, sectores, muelles y caminos viajan con ella en
   * vez de quedarse atrás y saltar de golpe al soltarla.
   */
  const origen = new Map(
    datos.naves.map((n) => {
      const movida = preview?.tipo === 'nave' && preview.id === String(n.id);
      return [n.id, movida ? { x: preview!.x, y: preview!.y } : { x: n.x, y: n.y }];
    }),
  );
  const naveDelSector = new Map(datos.sectores.map((s) => [s.id, s.nave]));

  /**
   * De coordenadas de la nave a coordenadas del predio, que es lo que dibuja
   * el SVG. Sin nave —un camino en la playa de maniobras— ya son globales.
   */
  const enElPredio = (naveId: number | null, p: { x: number; y: number }) => {
    const o = origen.get(naveId ?? -1) ?? { x: 0, y: 0 };
    return { x: p.x + o.x, y: p.y + o.y };
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`${-margen} ${-margen} ${ancho + margen * 2} ${largo + margen * 2}`}
      style={{
        width: '100%',
        height: 'auto',
        // Que entre en la pantalla: el dibujo se achica antes que aparecer un
        // scroll que corta la nave por la mitad.
        maxHeight: 'calc(100vh - 230px)',
        display: 'block',
        touchAction: 'none',
        background: c.fondo,
        borderRadius: 12,
        cursor: dibujando ? 'crosshair' : undefined,
      }}
      onPointerDown={onEmpezarTrazo}
      onPointerMove={onMover}
      onPointerUp={onSoltar}
      onPointerCancel={onSoltar}
    >
      {/* Grilla de un metro, para dar escala */}
      <defs>
        <pattern id="grilla" width="1" height="1" patternUnits="userSpaceOnUse">
          <path d="M 1 0 L 0 0 0 1" fill="none" stroke={c.borde} strokeWidth="0.03" />
        </pattern>
      </defs>

      {/* Una nave por rectángulo: el predio es lo que las contiene */}
      {datos.naves.map((n) => {
        const movida = preview?.tipo === 'nave' && preview.id === String(n.id);
        const x = movida ? preview!.x : n.x;
        const y = movida ? preview!.y : n.y;
        return (
          <g
            key={`n${n.id}`}
            onPointerDown={(e) => onBajarNave(e, n)}
            style={{ cursor: 'grab', pointerEvents: dibujando ? 'none' : undefined }}
          >
            <title>{`${n.nombre} · ${n.ancho} × ${n.largo} m`}</title>
            <rect
              x={x}
              y={y}
              width={n.ancho}
              height={n.largo}
              fill="#fff"
              stroke={c.bordeFuerte}
              strokeWidth={ESPESOR_PERIMETRAL_M}
            />
            <rect x={x} y={y} width={n.ancho} height={n.largo} fill="url(#grilla)" />
            <text x={x + 0.4} y={y + 1.3} fontSize={1.1} fill={c.texto3} style={{ fontWeight: 600 }}>
              {n.nombre}
            </text>
            {/* Cotas de la nave, cada una con las suyas */}
            <text x={x + n.ancho / 2} y={y - 0.6} fontSize={0.9} textAnchor="middle" fill={c.texto3}>
              {n.ancho} m
            </text>
            <text
              x={x - 0.7}
              y={y + n.largo / 2}
              fontSize={0.9}
              textAnchor="middle"
              fill={c.texto3}
              transform={`rotate(-90 ${x - 0.7} ${y + n.largo / 2})`}
            >
              {n.largo} m
            </text>
          </g>
        );
      })}

      {/* Paredes interiores: tabiques trazados a mano, con su espesor real
          (línea gruesa en metros, no en píxeles). Van sobre el piso de la
          nave, no se arrastran: se editan por la lista del panel. */}
      {datos.paredes.map((p) => {
        const o = origen.get(p.nave) ?? { x: 0, y: 0 };
        return (
          <line
            key={`p${p.id}`}
            x1={p.x1 + o.x}
            y1={p.y1 + o.y}
            x2={p.x2 + o.x}
            y2={p.y2 + o.y}
            stroke={c.bordeFuerte}
            strokeWidth={p.espesor}
            strokeLinecap="square"
            style={{ pointerEvents: 'none' }}
          />
        );
      })}

      {/* Aberturas: portones, puertas peatonales, ventanales y el paso entre
          naves, sobre su muro. Se pintan después de las naves y las paredes
          -en orden de documento, que es el orden en que el SVG las apila-
          para que el hueco de un portón tape de verdad el trazo que hay
          debajo, sea el de su propia nave o el de la de al lado: cuando dos
          naves están pegadas, el este de una y el oeste de la otra caen en
          el mismo lugar del predio, y pintar el hueco encima alcanza para
          que se vea abierto una sola vez, sin tener que enterarse de que el
          muro es compartido. */}
      {datos.naves.flatMap((n) =>
        LADOS.map((lado) => {
          const clave = claveDeLado(n.id, lado);
          const propias = datos.aberturas.filter((ab) => ab.muro === clave);
          if (propias.length === 0) return null;
          const local = ladoDeNave(n, lado);
          const a = enElPredio(n.id, local.a);
          const b = enElPredio(n.id, local.b);
          return (
            <g key={`ab-${clave}`} style={{ pointerEvents: 'none' }}>
              {propias.map((ab) => (
                <MarcaAbertura key={ab.id} a={a} b={b} espesor={ESPESOR_PERIMETRAL_M} abertura={ab} />
              ))}
            </g>
          );
        }),
      )}
      {datos.paredes.flatMap((p) => {
        const clave = claveDePared(p.id);
        const propias = datos.aberturas.filter((ab) => ab.muro === clave);
        if (propias.length === 0) return [];
        const o = origen.get(p.nave) ?? { x: 0, y: 0 };
        const a = { x: p.x1 + o.x, y: p.y1 + o.y };
        const b = { x: p.x2 + o.x, y: p.y2 + o.y };
        return (
          <g key={`ab-${clave}`} style={{ pointerEvents: 'none' }}>
            {propias.map((ab) => (
              <MarcaAbertura key={ab.id} a={a} b={b} espesor={p.espesor} abertura={ab} />
            ))}
          </g>
        );
      })}

      {/* Caminos: pintura de piso, van debajo de racks y muelles */}
      {datos.caminos.map((k) => {
        if (!k.ubicado) return null;
        const movido = preview?.tipo === 'camino' && preview.id === String(k.id);
        const local = movido ? { x: preview!.x, y: preview!.y } : { x: k.x!, y: k.y! };
        // Mientras se arrastra, el preview ya viene en coordenadas del predio:
        // es donde está el dedo. Guardado, en cambio, está en las de su nave.
        const { x, y } = movido ? local : enElPredio(k.nave, local);
        const paleta = colorCamino(k.tipo);
        return (
          <g
            key={`k${k.id}`}
            onPointerDown={(e) => onBajarCamino(e, k)}
            style={{ cursor: 'grab', pointerEvents: dibujando ? 'none' : undefined }}
          >
            <title>{k.nombre}</title>
            <rect
              x={x}
              y={y}
              width={k.ancho}
              height={k.largo}
              fill={paleta.relleno}
              stroke={paleta.borde}
              strokeWidth={0.08}
              rx={0.2}
            />
          </g>
        );
      })}

      {/* Muelles: bocas de carga sobre la pared, se arrastran igual que un rack */}
      {datos.muelles.map((m) => {
        if (!m.ubicado) return null;
        const movido = preview?.tipo === 'muelle' && preview.id === m.id;
        const local = movido ? { x: preview!.x, y: preview!.y } : { x: m.x!, y: m.y! };
        // Igual que el camino: el preview es del predio, lo guardado es de su nave.
        const { x, y } = movido ? local : enElPredio(m.nave, local);
        return (
          <g
            key={m.id}
            onPointerDown={(e) => onBajarMuelle(e, m)}
            style={{ cursor: 'grab', pointerEvents: dibujando ? 'none' : undefined }}
          >
            <title>{`Muelle ${m.id}`}</title>
            <rect
              x={x}
              y={y}
              width={m.ancho}
              height={m.largo}
              fill="#dfe4ea"
              stroke={c.bordeFuerte}
              strokeWidth={0.08}
              rx={0.2}
            />
            <text
              x={x + m.ancho / 2}
              y={y + m.largo / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={Math.min(0.8, m.largo * 0.6)}
              fill={c.texto3}
            >
              {m.id}
            </text>
          </g>
        );
      })}

      {/* Zona de cada sector: la dibujada, o la que envuelve a sus racks */}
      {datos.sectores.map((s) => {
        if (!s.area) return null;
        const movido = preview?.tipo === 'sector' && preview.id === s.id;
        const p = movido ? { x: preview!.x, y: preview!.y } : enElPredio(s.nave, s.area);
        const a = { ...s.area, x: p.x, y: p.y };
        return (
          <g
            key={s.id}
            onPointerDown={(e) => onBajarSector(e, s)}
            style={{
              cursor: s.dibujada ? 'grab' : 'default',
              pointerEvents: dibujando || !s.dibujada ? 'none' : undefined,
            }}
          >
            <title>{`${s.nombre}${s.dibujada ? ' · zona dibujada' : ' · área tomada de sus racks'}`}</title>
            <rect
              x={s.dibujada ? a.x : a.x - 0.6}
              y={s.dibujada ? a.y : a.y - 0.6}
              width={s.dibujada ? a.ancho : a.ancho + 1.2}
              height={s.dibujada ? a.alto : a.alto + 1.2}
              fill={colorDe(s.id)}
              fillOpacity={s.dibujada ? 0.1 : 0.05}
              stroke={colorDe(s.id)}
              strokeOpacity={0.35}
              strokeWidth={0.1}
              strokeDasharray="0.5 0.4"
              rx={0.5}
            />
            <text x={a.x - 0.4} y={a.y - 1.1} fontSize={1.1} fill={colorDe(s.id)} style={{ fontWeight: 600 }}>
              {s.nombre}
            </text>
          </g>
        );
      })}

      {/* Racks */}
      {datos.racks.map((r) => {
        if (!r.huella) return null;
        const enVuelo = preview?.tipo === 'rack' && preview.id === r.id;
        // El rack no guarda nave: la suya es la de su sector.
        const h = enVuelo
          ? { ...r.huella, x: preview!.x, y: preview!.y }
          : { ...r.huella, ...enElPredio(naveDelSector.get(r.sector) ?? null, r.huella) };
        const color = colorDe(r.sector);
        // Una zona de piso tiene un solo lugar y muchos pallets: se topea en lleno.
        const lleno = r.lugares > 0 ? Math.min(1, r.ocupados / r.lugares) : 0;

        return (
          <g
            key={r.id}
            onPointerDown={(e) => onBajar(e, r)}
            style={{ cursor: 'grab', pointerEvents: dibujando ? 'none' : undefined }}
          >
            <rect
              x={h.x}
              y={h.y}
              width={h.ancho}
              height={h.alto}
              fill={color}
              fillOpacity={0.18 + lleno * 0.5}
              stroke={color}
              strokeWidth={seleccion === r.id ? 0.22 : 0.1}
              rx={0.15}
              // El borde del elegido engorda de a poco. Sólo el trazo y el
              // relleno: nada de geometría, que es lo que se arrastra.
              style={{ transition: 'stroke-width .25s, fill-opacity .35s' }}
            />
            {/* Separadores de posición, para que se lea cuántas hay */}
            {Array.from({ length: r.columnas - 1 }, (_, i) => {
              const d = (i + 1) * r.anchoPosicion;
              return h.ancho > h.alto ? (
                <line key={i} x1={h.x + d} y1={h.y} x2={h.x + d} y2={h.y + h.alto} stroke="#fff" strokeWidth={0.04} />
              ) : (
                <line key={i} x1={h.x} y1={h.y + d} x2={h.x + h.ancho} y2={h.y + d} stroke="#fff" strokeWidth={0.04} />
              );
            })}
            <text
              x={h.x + h.ancho / 2}
              y={h.y + h.alto / 2 + 0.35}
              fontSize={Math.min(1.1, h.alto * 0.7)}
              textAnchor="middle"
              fill="#fff"
              style={{ fontWeight: 700, pointerEvents: 'none' }}
            >
              {r.id}
            </text>
          </g>
        );
      })}

      {trazo && colorTrazo && (
        <g style={{ pointerEvents: 'none' }}>
          <rect
            x={trazo.x}
            y={trazo.y}
            width={trazo.ancho}
            height={trazo.largo}
            fill={colorTrazo.relleno}
            fillOpacity={0.75}
            stroke={colorTrazo.borde}
            strokeWidth={0.12}
            strokeDasharray="0.6 0.4"
            rx={0.2}
          />
          <text
            x={trazo.x + trazo.ancho / 2}
            y={trazo.y + trazo.largo / 2}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={0.9}
            fill={c.texto2}
          >
            {trazo.ancho} × {trazo.largo} m
          </text>
        </g>
      )}

      {/* El trazo de una pared es un segmento: se dibuja en su espesor real,
          igual que las paredes ya guardadas, para que se vea cómo va a quedar. */}
      {trazoPared && (
        <g style={{ pointerEvents: 'none' }}>
          <line
            x1={trazoPared.a.x}
            y1={trazoPared.a.y}
            x2={trazoPared.b.x}
            y2={trazoPared.b.y}
            stroke={c.bordeFuerte}
            strokeWidth={datos.paredPorDefecto.espesorM}
            strokeLinecap="square"
            strokeDasharray="0.6 0.4"
          />
          <text
            x={(trazoPared.a.x + trazoPared.b.x) / 2}
            y={(trazoPared.a.y + trazoPared.b.y) / 2 - 0.4}
            textAnchor="middle"
            fontSize={0.9}
            fill={c.texto2}
          >
            {Math.hypot(trazoPared.b.x - trazoPared.a.x, trazoPared.b.y - trazoPared.a.y)} m
          </text>
        </g>
      )}
    </svg>
  );
}

/**
 * Una abertura sobre su muro, entre `a` y `b` (los dos puntos del muro, ya en
 * coordenadas del predio). El portón, la puerta y el paso llegan al piso: se
 * pintan con un trazo del color de fondo, un poco más ancho que el muro, que
 * tapa lo que hay debajo —el muro y la grilla de la nave— y deja ver el
 * hueco de verdad. El ventanal tiene antepecho: abajo la pared sigue estando,
 * así que no se tapa nada y sólo se agrega la línea fina de encima. En los
 * dos casos esa línea fina, en el color del tipo, es la que hace que la
 * abertura se lea sobre el plano.
 */
function MarcaAbertura({ a, b, espesor, abertura }: { a: Punto; b: Punto; espesor: number; abertura: AberturaPlanta }) {
  const largo = Math.hypot(b.x - a.x, b.y - a.y);
  if (largo === 0) return null;
  const t0 = Math.max(0, Math.min(largo, abertura.offset)) / largo;
  const t1 = Math.max(0, Math.min(largo, abertura.offset + abertura.ancho)) / largo;
  const p0 = { x: a.x + (b.x - a.x) * t0, y: a.y + (b.y - a.y) * t0 };
  const p1 = { x: a.x + (b.x - a.x) * t1, y: a.y + (b.y - a.y) * t1 };

  // El muro se movió o se achicó después de colocar la abertura, y el clamp
  // de arriba la deja de largo cero: invisible acá, aunque siga entera en la
  // lista del panel y en el 3D. El clamp está bien -no hay nada más seguro
  // para dibujar-, lo que está mal es callarlo: se marca el punto con un
  // círculo de aviso en vez de no dibujar nada.
  if (t0 === t1) {
    return (
      <circle cx={p0.x} cy={p0.y} r={0.3} fill="none" stroke={c.rojo} strokeWidth={0.07} strokeDasharray="0.12 0.1">
        <title>{`${abertura.nombre || abertura.tipo} se salió de su muro: no entra en el plano. Ajustala en la lista de aberturas.`}</title>
      </circle>
    );
  }

  return (
    <>
      {llegaAlPiso(abertura) && (
        <line x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke={c.fondo} strokeWidth={espesor + 0.06} />
      )}
      <line
        x1={p0.x}
        y1={p0.y}
        x2={p1.x}
        y2={p1.y}
        stroke={colorAbertura(abertura.tipo)}
        strokeWidth={Math.min(0.12, Math.max(0.04, espesor * 0.5))}
      />
    </>
  );
}
